/**
 * Stable cache segment for a period report (income statement, cash flow): a
 * preset id, or the explicit `from..to` window. Two requests with the same
 * resolved window but different inputs (e.g. a preset vs the dates it resolves
 * to) are deliberately cached separately, since the request differs.
 */
function periodReportKey(range) {
    return range.period !== undefined ? `period:${range.period}` : `range:${range.from}..${range.to}`;
}
/**
 * Stable cache segment for a period-set report (the income-statement
 * series): the `periodSet` discriminant plus whichever of its own optional
 * fields are set, so a rolling default (12 months, latest completed month)
 * caches separately from an explicit `endingMonth`/`monthCount`, and a
 * financial-year request caches separately by `cutoffMonth`/`comparatives`.
 */
function reportPeriodSetKey(input) {
    return input.periodSet === 'rolling'
        ? `rolling:${input.endingMonth ?? 'latest'}:${input.monthCount ?? 12}`
        : `financial-year:${input.cutoffMonth ?? 'latest'}:${input.comparatives ?? false}`;
}
/**
 * Stable cache segment for an as-at report (balance sheet, trial balance): a
 * preset id, the explicit `asOf` date, or `all-time` when no input is supplied
 * (trial balance only).
 */
function asOfReportKey(at) {
    if (at === undefined)
        return 'all-time';
    return at.asOfPreset !== undefined ? `preset:${at.asOfPreset}` : `asOf:${at.asOf}`;
}
export const queryKeys = {
    me: ['me'],
    user: (userId) => ['user', userId],
    oauthAccounts: () => ['oauthAccounts'],
    accounts: ['accounts'],
    account: (id) => ['account', id],
    invites: (accountId) => ['invites', accountId],
    emailInboxes: (accountId) => ['emailInboxes', accountId],
    emailInboxFolders: (accountId) => ['emailInboxFolders', accountId],
    calendarConnections: (accountId) => ['calendarConnections', accountId],
    clientFeeEarnerRates: (accountId, clientId) => ['clientFeeEarnerRates', accountId, clientId],
    clientProjectFeeEarnerRates: (accountId, clientProjectId) => ['clientProjectFeeEarnerRates', accountId, clientProjectId],
    resolvedRate: (accountId, query) => ['resolvedRate', accountId, query.clientProjectId, query.performedByUserId ?? null, query.unitType, query.activityType ?? null],
    resolvedRatePrefix: (accountId) => ['resolvedRate', accountId],
    clients: (accountId) => ['clients', accountId],
    client: (accountId, clientId) => ['client', accountId, clientId],
    clientWipTotal: (accountId, clientId) => ['clientWipTotal', accountId, clientId],
    clientWipTotals: (accountId) => ['clientWipTotal', accountId],
    clientProjectWipTotal: (accountId, clientProjectId) => ['clientProjectWipTotal', accountId, clientProjectId],
    /** Account-scoped prefix of every `clientProjectWipTotal(accountId, ...)` key. */
    clientProjectWipTotals: (accountId) => ['clientProjectWipTotal', accountId],
    clientOutstandingBalance: (accountId, clientId) => ['clientOutstandingBalance', accountId, clientId],
    clientOutstandingBalances: (accountId) => ['clientOutstandingBalance', accountId],
    /**
     * The client and matter hub's reads all live under one account-scoped prefix,
     * so a mutation that moves any hub figure clears them with one call. A
     * namespace of its own, not nested under `clientProjects`: the walkers that
     * patch cached lists under that prefix are typed for `ClientProject` rows.
     */
    clientOverviewPrefix: (accountId) => ['clientOverview', accountId],
    clientOverview: (accountId, clientId) => ['clientOverview', accountId, 'client', clientId],
    clientProjectOverview: (accountId, clientProjectId) => ['clientOverview', accountId, 'clientProject', clientProjectId],
    /**
     * A matter's statement. Under the hub prefix so every mutation that moves a
     * hub figure (the AR-moving ones all do) refreshes it too.
     */
    clientProjectStatement: (accountId, clientProjectId, from, to) => ['clientOverview', accountId, 'clientProjectStatement', clientProjectId, from, to],
    /**
     * A client's matters summary. Read through a `useInfiniteQuery`, so the key
     * is CURSOR-FREE (the cursor travels as `pageParam`; see `cashbookReport`).
     * Fields are normalised to their server defaults so the same list asked for
     * with and without an explicit default shares one entry.
     */
    clientProjectsSummary: (accountId, clientId, query) => [
        'clientOverview',
        accountId,
        'clientProjectsSummary',
        clientId,
        query.search ?? null,
        query.status ?? 'active',
        query.sort ?? null,
        query.direction ?? null,
        query.limit ?? null,
    ],
    /**
     * One timeline filter's feed, read through a `useInfiniteQuery` (cursor-free
     * key). `sources` is the chip's source, or every source for "All".
     */
    clientOverviewTimeline: (accountId, scope, sources) => ['clientOverview', accountId, 'timeline', scope, sources],
    /** The number on one timeline chip. */
    clientOverviewTimelineCount: (accountId, scope, source) => ['clientOverview', accountId, 'timelineCount', scope, source],
    suppliers: (accountId) => ['suppliers', accountId],
    supplier: (accountId, supplierId) => ['supplier', accountId, supplierId],
    /** A supplier's active bank accounts - the "On file" comparison side of SupplierBankSnapshotPanel. */
    supplierBankAccounts: (accountId, supplierId) => ['supplierBankAccounts', accountId, supplierId],
    /** One supplier's own expenses, as rendered on the supplier page. */
    supplierExpenses: (accountId, supplierId) => ['supplierExpenses', accountId, supplierId],
    /**
     * Account-scoped prefix of every `supplierOnAccountBalance` key. Used by
     * mutations that reverse or void a `supplierOnAccount`-funded payment without
     * knowing which supplier it applied to (the funding bank account is not
     * held client-side either), so every cached cap is invalidated together.
     */
    supplierOnAccountBalancePrefix: (accountId) => ['supplierOnAccountBalance', accountId],
    /** The amount paid to the supplier on account - the payable-side mirror of
     *  `availableCredit` below. Invalidated whenever a `supplierOnAccount`-funded
     *  payment is recorded so the funding picker's cap refreshes. */
    suggestedExpenseAccount: (accountId, supplierId) => ['suggestedExpenseAccount', accountId, supplierId],
    supplierOnAccountBalance: (accountId, supplierId) => [...queryKeys.supplierOnAccountBalancePrefix(accountId), supplierId],
    /**
     * Supplier-scoped prefix of every `supplierStatement` key, regardless of
     * period params. Invalidate this to clear all cached statement periods for
     * one supplier after a payment, credit note, or journal edit that affects AP.
     */
    supplierStatementPrefix: (accountId, supplierId) => ['supplierStatement', accountId, supplierId],
    /**
     * One supplier's AP statement for a specific period. Both `from` and `to`
     * are optional slots - omitted means "full history" and "today" respectively,
     * so every period variant caches separately.
     */
    supplierStatement: (accountId, supplierId, from, to) => [...queryKeys.supplierStatementPrefix(accountId, supplierId), from, to],
    supplierInvoices: (accountId) => ['supplierInvoices', accountId],
    /**
     * One shared cache entry per expense, read by every caller (the detail
     * page, `ExpenseDetailPreview`, and the line/allocation-editing hooks
     * nested under the detail page's form). A variant segment was tried here
     * (splitting on `includeVoided`) and reverted: `setQueryData`,
     * `getQueryData`, and `cancelQueries` in `useExpenseLineEditing.ts` and
     * `useLineItemGlAllocation.ts` target this EXACT key (unlike
     * `invalidateQueries`, they do not prefix-match), so a caller-specific
     * variant silently broke the optimistic line-edit and GL-allocation
     * patches on the detail page - the write landed on a cache entry nobody
     * was observing. Every caller that reads a single expense by id must
     * fetch it the SAME way (see `getSupplierInvoice`'s `includeVoided`
     * option) so this one key is safe to share.
     */
    supplierInvoicePrefix: (accountId) => ['supplierInvoice', accountId],
    billableExpensesPrefix: (accountId) => ['billableExpenses', accountId],
    supplierInvoice: (accountId, supplierInvoiceId) => ['supplierInvoice', accountId, supplierInvoiceId],
    billableExpenses: (accountId, clientId) => ['billableExpenses', accountId, clientId],
    supplierInvoiceDocuments: (accountId, supplierInvoiceId) => ['supplierInvoiceDocuments', accountId, supplierInvoiceId],
    supplierInvoiceExtraction: (accountId, extractionId) => ['supplierInvoiceExtraction', accountId, extractionId],
    /**
     * Separate key from `supplierInvoiceExtraction` because the transcribed lines
     * are a separate endpoint - they are deliberately kept off the extraction
     * response, which doubles as the chat/MCP tool contract. Fetched once the job
     * is COMPLETED, so it must not share the polled key's cache entry.
     */
    supplierInvoiceExtractionLineItems: (accountId, extractionId) => ['supplierInvoiceExtractionLineItems', accountId, extractionId],
    clientProjects: (accountId) => ['clientProjects', accountId],
    clientProject: (accountId, projectId) => ['clientProject', accountId, projectId],
    /** Account-scoped prefix of every `clientProject(accountId, ...)` key. */
    clientProjectPrefix: (accountId) => ['clientProject', accountId],
    clientProjectActivities: (accountId) => ['clientProjectActivities', accountId],
    clientProjectActivity: (accountId, activityId) => ['clientProjectActivity', accountId, activityId],
    invoices: (accountId) => ['invoices', accountId],
    invoice: (accountId, invoiceId) => ['invoice', accountId, invoiceId],
    invoiceSends: (accountId, invoiceId) => ['invoiceSends', accountId, invoiceId],
    invoiceEligibleAttachments: (accountId, invoiceId) => ['invoiceEligibleAttachments', accountId, invoiceId],
    invoiceCreditNotes: (accountId, invoiceId) => ['invoiceCreditNotes', accountId, invoiceId],
    invoiceCorrections: (accountId, invoiceId) => ['invoiceCorrections', accountId, invoiceId],
    // Batch key for "which invoice is this line item on" (pro forma lock
    // partition, the activity linked-invoice alert, the omnibox). Callers pass a
    // sorted id list so the key is stable for the same set regardless of row
    // order - same shape as storedEmailsByIds.
    invoicesByLineItemIds: (accountId, sortedLineItemIds) => ['invoicesByLineItemIds', accountId, sortedLineItemIds],
    creditNote: (accountId, creditNoteId) => ['creditNote', accountId, creditNoteId],
    creditNotes: (accountId) => ['creditNotes', accountId],
    ledgers: (accountId) => ['ledgers', accountId],
    feeEarnerFeeAccounts: (accountId) => ['feeEarnerFeeAccounts', accountId],
    ledger: (accountId, ledgerId) => ['ledger', accountId, ledgerId],
    ledgerAccounts: (accountId, ledgerId) => ['ledgerAccounts', accountId, ledgerId],
    assetCategories: (accountId, ledgerId) => ['assetCategories', accountId, ledgerId],
    assets: (accountId, ledgerId) => ['assets', accountId, ledgerId],
    asset: (accountId, ledgerId, assetId) => ['asset', accountId, ledgerId, assetId],
    assetDisposal: (accountId, ledgerId, assetId) => ['assetDisposal', accountId, ledgerId, assetId],
    assetDepreciationSchedule: (accountId, ledgerId, assetId) => ['assetDepreciationSchedule', accountId, ledgerId, assetId],
    /**
     * Archived-inclusive variant of `ledgerAccounts`, for a reader that must
     * resolve an account by id regardless of archive state (e.g. naming an
     * archived account on a journal leg) - never for a picker or filter
     * dropdown, which must keep reading the active-only `ledgerAccounts` key.
     * Shares the `'ledgerAccounts'` prefix so an `invalidateQueries` call keyed
     * on the base `ledgerAccounts(accountId, ledgerId)` still matches this
     * variant (React Query's default prefix match), keeping both caches in
     * sync after any account-mutating action - archive included.
     * An optional `systemRole` is a further tuple member so a BANK-only
     * payload cannot reuse the full-chart cache; the two-arg key is unchanged.
     */
    ledgerAccountsIncludingArchived: (accountId, ledgerId, systemRole) => systemRole === undefined
        ? ['ledgerAccounts', accountId, ledgerId, 'includingArchived']
        : ['ledgerAccounts', accountId, ledgerId, 'includingArchived', systemRole],
    /** Flat account-group rows for a ledger (the structure-editing UI's read). */
    accountGroups: (accountId, ledgerId) => ['accountGroups', accountId, ledgerId],
    /** One ledger account's own group. Separate from `accountGroups` (the flat
     *  rows) because it answers "which group is THIS account in", which no other
     *  read exposes per account. */
    ledgerAccountGroup: (accountId, ledgerId, ledgerAccountId) => ['ledgerAccountGroup', accountId, ledgerId, ledgerAccountId],
    ledgerAccountGroupPrefix: (accountId, ledgerId) => ['ledgerAccountGroup', accountId, ledgerId],
    /** The assembled grouped trial balance (task #198). Structural group ops
     *  invalidate this alongside `accountGroups` - two views over the same rows. */
    groupedTrialBalance: (accountId, ledgerId, at, showZeroBalances) => ['groupedTrialBalance', accountId, ledgerId, asOfReportKey(at), showZeroBalances ?? null],
    groupedTrialBalancePrefix: (accountId, ledgerId) => ['groupedTrialBalance', accountId, ledgerId],
    groupedIncomeStatement: (accountId, ledgerId, range) => ['grouped-income-statement', accountId, ledgerId, periodReportKey(range)],
    groupedIncomeStatementPrefix: (accountId, ledgerId) => ['grouped-income-statement', accountId, ledgerId],
    groupedIncomeStatementSeries: (accountId, ledgerId, input) => ['grouped-income-statement-series', accountId, ledgerId, reportPeriodSetKey(input)],
    groupedIncomeStatementSeriesPrefix: (accountId, ledgerId) => ['grouped-income-statement-series', accountId, ledgerId],
    groupedBalanceSheet: (accountId, ledgerId, at) => ['grouped-balance-sheet', accountId, ledgerId, asOfReportKey(at)],
    groupedBalanceSheetPrefix: (accountId, ledgerId) => ['grouped-balance-sheet', accountId, ledgerId],
    groupedBalanceSheetSeries: (accountId, ledgerId, at, compare) => ['grouped-balance-sheet-series', accountId, ledgerId, asOfReportKey(at), compare],
    groupedBalanceSheetSeriesPrefix: (accountId, ledgerId) => ['grouped-balance-sheet-series', accountId, ledgerId],
    /**
     * `showZeroBalances` is folded in as its own segment (not part of
     * `asOfReportKey`) so the two toggle states on TrialBalancePage cache
     * separately from each other and from the AccountsTab's no-argument entry.
     */
    trialBalance: (accountId, ledgerId, at, showZeroBalances) => ['trialBalance', accountId, ledgerId, asOfReportKey(at), showZeroBalances ?? null],
    /**
     * Ledger-scoped prefix matching EVERY `trialBalance` entry: all as-at
     * variants and both `showZeroBalances` states. Balance-changing mutations
     * (journal post, account edit/archive, imports) must invalidate with this
     * prefix - the fully-specified `trialBalance` key resolves the optional
     * segments to `'all-time'`/`null` and only ever matches the AccountsTab
     * variant, leaving the report page's entries stale.
     */
    trialBalancePrefix: (accountId, ledgerId) => ['trialBalance', accountId, ledgerId],
    /**
     * The multi-date trial balance. A separate namespace from `trialBalance`, not
     * a variant of it: the two return different shapes, and a mutation that
     * invalidates one must invalidate the other, which `trialBalancePrefix` alone
     * would not do.
     */
    trialBalanceSeries: (accountId, ledgerId, at, compare, showZeroBalances) => ['trialBalanceSeries', accountId, ledgerId, asOfReportKey(at), compare, showZeroBalances ?? null],
    /** Ledger-scoped prefix matching every anchor, comparison and toggle state. */
    trialBalanceSeriesPrefix: (accountId, ledgerId) => ['trialBalanceSeries', accountId, ledgerId],
    /**
     * Both age-analysis reports share the `age-analysis` cache namespace, with
     * the party type (`debtors`/`creditors`) as a segment AFTER `accountId`/
     * `ledgerId` rather than the leading segment - this is what lets
     * `ageAnalysisPrefix` invalidate every as-at variant of BOTH reports with
     * one prefix-matched call (see its JSDoc below).
     */
    debtorsAgeAnalysis: (accountId, ledgerId, at) => ['age-analysis', accountId, ledgerId, 'debtors', asOfReportKey(at)],
    creditorsAgeAnalysis: (accountId, ledgerId, at) => ['age-analysis', accountId, ledgerId, 'creditors', asOfReportKey(at)],
    /**
     * Ledger-scoped prefix matching EVERY as-at variant of BOTH the debtors and
     * creditors age analysis (see `trialBalancePrefix`). A journal post can
     * move either the AR or the AP control account, so a balance-changing
     * mutation invalidates both reports together rather than picking one.
     */
    ageAnalysisPrefix: (accountId, ledgerId) => ['age-analysis', accountId, ledgerId],
    cashFlow: (accountId, ledgerId, range) => ['cash-flow', accountId, ledgerId, periodReportKey(range)],
    cashFlowSeries: (accountId, ledgerId, input) => ['cash-flow-series', accountId, ledgerId, reportPeriodSetKey(input)],
    /** Ledger-scoped prefix matching every range variant of `cashFlow` (see `trialBalancePrefix`). */
    cashFlowPrefix: (accountId, ledgerId) => ['cash-flow', accountId, ledgerId],
    /** Ledger-scoped prefix matching every monthly-series variant of `cashFlowSeries`. */
    cashFlowSeriesPrefix: (accountId, ledgerId) => ['cash-flow-series', accountId, ledgerId],
    /**
     * The cashbook is read through a `useInfiniteQuery`, so the key must stay
     * CURSOR-FREE: the cursor travels as `pageParam` and every page of one
     * report window belongs under the same key. Folding it in would give each
     * page its own cache entry, so "Load more" would refetch page one forever.
     * `Omit<..., 'cursor'>` says so at the type level rather than silently
     * dropping a field a caller thought it had passed.
     */
    cashbookReport: (accountId, ledgerId, query) => [
        'cashbookReport',
        accountId,
        ledgerId,
        query.from ?? null,
        query.to,
        query.bankAccountId ?? null,
        query.limit,
        query.ledgerAccountId ?? null,
        query.unallocatedOnly ?? null,
        query.search ?? null,
        query.sortBy,
        query.sortDirection ?? null,
    ],
    /**
     * Ledger-scoped prefix matching every filter/sort variant of
     * `cashbookReport` (see `trialBalancePrefix`).
     */
    cashbookReportPrefix: (accountId, ledgerId) => ['cashbookReport', accountId, ledgerId],
    indirectCashFlow: (accountId, ledgerId, range) => ['indirect-cash-flow', accountId, ledgerId, periodReportKey(range)],
    indirectCashFlowSeries: (accountId, ledgerId, input) => ['indirect-cash-flow-series', accountId, ledgerId, reportPeriodSetKey(input)],
    /** Ledger-scoped prefix matching every range variant of `indirectCashFlow` (see `cashFlowPrefix`). */
    indirectCashFlowPrefix: (accountId, ledgerId) => ['indirect-cash-flow', accountId, ledgerId],
    /** Ledger-scoped prefix matching every monthly-series variant of `indirectCashFlowSeries`. */
    indirectCashFlowSeriesPrefix: (accountId, ledgerId) => ['indirect-cash-flow-series', accountId, ledgerId],
    ledgerEntrySets: (accountId, ledgerId) => ['ledgerEntrySets', accountId, ledgerId],
    ledgerEntrySet: (accountId, ledgerId, entrySetId) => ['ledgerEntrySet', accountId, ledgerId, entrySetId],
    /**
     * The acquisition-journal picker's paginated search, scoped under the
     * `ledgerEntrySets` prefix so a mutation that invalidates the whole journal
     * list (posting or reversing one) also invalidates every open picker.
     */
    ledgerEntrySetPicker: (accountId, ledgerId, params) => [...queryKeys.ledgerEntrySets(accountId, ledgerId), 'picker', params],
    /**
     * Documents attached to a journal (manual ledger entry set). Scoped by
     * entrySetId alone - the id is already unique per account, mirroring
     * `supplierInvoiceDocuments` / `bankStatementLineDocuments`.
     */
    ledgerEntrySetDocuments: (accountId, entrySetId) => ['ledgerEntrySetDocuments', accountId, entrySetId],
    payments: (accountId) => ['payments', accountId],
    payment: (accountId, paymentId) => ['payment', accountId, paymentId],
    storedEmail: (accountId, storedEmailId) => ['storedEmail', accountId, storedEmailId],
    // Batch keys for the diary's source-preview fetches. Callers pass a sorted
    // id list so the key is stable for the same set regardless of row order.
    storedEmailsByIds: (accountId, sortedIds) => ['storedEmailsByIds', accountId, sortedIds],
    storedCalendarEvent: (accountId, storedCalendarEventId) => ['storedCalendarEvent', accountId, storedCalendarEventId],
    storedCalendarEventsByIds: (accountId, sortedIds) => ['storedCalendarEventsByIds', accountId, sortedIds],
    subscriptionPlans: (accountId) => ['subscriptionPlans', accountId],
    subscription: (accountId) => ['subscription', accountId],
    aiUsage: (accountId) => ['aiUsage', accountId],
    conversations: (accountId) => ['conversations', accountId],
    conversation: (accountId, conversationId) => ['conversation', accountId, conversationId],
    conversationMessages: (accountId, conversationId) => ['conversationMessages', accountId, conversationId],
    dashboardTotals: (accountId) => ['dashboardTotals', accountId],
    xeroConnection: (accountId) => ['xeroConnection', accountId],
    myConnectedApps: (accountId) => ['connectedApps', 'mine', accountId],
    accountConnectedApps: (accountId) => ['connectedApps', 'account', accountId],
    invoiceWordExportFeature: (accountId) => ['invoiceWordExportFeature', accountId],
    xeroAccounts: (accountId) => ['xeroAccounts', accountId],
    bankAccounts: (accountId) => ['bank-accounts', accountId],
    clientAccountPaymentFundingOptions: (accountId) => ['client-account-payment-funding-options', accountId],
    bankStatements: (accountId, bankAccountId) => ['bank-statements', accountId, bankAccountId],
    bankStatement: (accountId, statementId) => ['bank-statement', accountId, statementId],
    bankStatementReview: (accountId, statementId) => ['bank-statement-review', accountId, statementId],
    /**
     * One bank statement line, addressed by its own id alone. Distinct namespace
     * from the `bankStatementLines` list below, which is keyed per bank account -
     * this is the read for a caller that does not yet know the bank account.
     */
    bankStatementLine: (accountId, lineId) => ['bank-statement-line', accountId, lineId],
    bankStatementLines: (accountId, bankAccountId, query) => [
        'bank-statement-lines',
        accountId,
        bankAccountId,
        query?.page ?? null,
        query?.limit ?? null,
        query?.from ?? null,
        query?.to ?? null,
        query?.bankStatementId ?? null,
        query?.allocated ?? null,
        query?.hasProposal ?? null,
        query?.order ?? null,
        query?.search ?? null,
    ],
    statementAllocationSummary: (accountId, statementId) => ['statementAllocationSummary', accountId, statementId],
    bankAccountUnallocatedCount: (accountId, bankAccountId) => ['bankAccountUnallocatedCount', accountId, bankAccountId],
    /**
     * Two-element prefix matching every bank account's unallocated count on the
     * account. For a mutation that moves the figure without naming which bank it
     * moved (a void whose funding bank is not known client-side), where the
     * single-id key cannot be built.
     */
    bankAccountUnallocatedCountPrefix: (accountId) => ['bankAccountUnallocatedCount', accountId],
    bankAccountUnallocatedCounts: (accountId, bankAccountIds) => ['bankAccountUnallocatedCounts', accountId, [...bankAccountIds].sort()],
    /**
     * Two-element prefix matching every id-set variant of the batch above. A
     * mutation that changes the account's bank-account id set (e.g. a create)
     * cannot name the NEXT variant's key, so it invalidates by this prefix
     * instead of the full three-element key.
     */
    bankAccountUnallocatedCountsPrefix: (accountId) => ['bankAccountUnallocatedCounts', accountId],
    /**
     * Imported-line count and transaction-date span for one bank account. Moves
     * only when a statement is imported or deleted, so it is not invalidated by
     * allocation mutations (which change what a line is assigned to, never
     * whether the line exists or what date it carries).
     */
    bankAccountImportedLineCoverage: (accountId, bankAccountId) => ['bankAccountImportedLineCoverage', accountId, bankAccountId],
    /** Two-element prefix over every bank account's coverage (see `bankAccountUnallocatedCountPrefix`). */
    bankAccountImportedLineCoveragePrefix: (accountId) => ['bankAccountImportedLineCoverage', accountId],
    bankAccountImportedLineCoverageBatch: (accountId, bankAccountIds) => ['bankAccountImportedLineCoverageBatch', accountId, [...bankAccountIds].sort()],
    /** Two-element prefix matching every id-set variant of the batch above (see `bankAccountUnallocatedCountsPrefix`). */
    bankAccountImportedLineCoverageBatchPrefix: (accountId) => ['bankAccountImportedLineCoverageBatch', accountId],
    /**
     * Confirmed, signed pending, signed uncategorised balance, and pending
     * drill-down for one bank account.
     * A mutation that does not know which bank account funded/settled a payment
     * (e.g. voiding a payment) invalidates the two-element prefix
     * `['bankAccountBalanceSummary', accountId]` instead of this precise key -
     * that invalidates every cached bank account's summary for the tenant, which
     * is cheap and correct without knowing the specific bank.
     */
    bankAccountBalanceSummary: (accountId, bankAccountId) => ['bankAccountBalanceSummary', accountId, bankAccountId],
    /** Two-element prefix over every bank account's balance summary (see `bankAccountUnallocatedCountPrefix`). */
    bankAccountBalanceSummaryPrefix: (accountId) => ['bankAccountBalanceSummary', accountId],
    bankAccountConfirmedBalances: (accountId, bankAccountIds) => ['bankAccountConfirmedBalances', accountId, [...bankAccountIds].sort()],
    /** Two-element prefix matching every id-set variant of the batch above (see `bankAccountUnallocatedCountsPrefix`). */
    bankAccountConfirmedBalancesPrefix: (accountId) => ['bankAccountConfirmedBalances', accountId],
    matchableExpensesForLine: (accountId, lineId, options) => ['matchableExpensesForLine', accountId, lineId, options.search, options.limit],
    matchableCreditNotesForLine: (accountId, lineId, options) => ['matchableCreditNotesForLine', accountId, lineId, options.search, options.limit],
    reconcilableInvoicesForLine: (accountId, lineId, options) => ['reconcilableInvoicesForLine', accountId, lineId, options.search, options.limit],
    reconcilableSupplierInvoicesForLine: (accountId, lineId, options) => ['reconcilableSupplierInvoicesForLine', accountId, lineId, options.search, options.limit],
    completableTransfersForLine: (accountId, lineId) => ['completableTransfersForLine', accountId, lineId],
    transferForLine: (accountId, lineId) => ['transferForLine', accountId, lineId],
    /**
     * Batched allocation read-back for one PAGE GROUP of `AllocationSectionTable`
     * - the page-shaped replacement for the per-row matched-invoices /
     * matched-expenses / matched-credit-notes / client-allocations / transfer /
     * entry-set reads. Keyed by POSITION (`filterFingerprint`, `groupIndex`,
     * `pageCount`), never by the matching line ids themselves: an `include`
     * predicate reading a line's `ledgerEntrySetId` (as this read's does) would
     * otherwise mint a new key - and strand every other row sharing the old
     * one under a pending placeholder - the moment any sibling row on the
     * group's page(s) is allocated. `pageCount` (how many pages currently
     * populate the group, from `pageGroupPageCount` in
     * `allocation-section-table.helpers.ts`) is the one thing that DOES need to
     * change the key: it is what makes a still-filling group re-fetch once,
     * when "Load more" completes it, without depending on line-level state at
     * all. `filterFingerprint` scopes a filter/tab change to its own fresh set
     * of groups rather than re-showing the previous filter's cached ones.
     */
    bankStatementLineAllocationDetails: (accountId, filterFingerprint, groupIndex, pageCount) => ['bankStatementLineAllocationDetails', accountId, filterFingerprint, groupIndex, pageCount],
    /** Two-element prefix matching every page-group variant of the batch above. */
    bankStatementLineAllocationDetailsPrefix: (accountId) => ['bankStatementLineAllocationDetails', accountId],
    /** See `bankLineProposalsByLines` below - same position-keyed shape, scoped additionally to the bank account. */
    suggestAllocations: (accountId, bankAccountId, filterFingerprint, groupIndex, pageCount) => ['suggestAllocations', accountId, bankAccountId, filterFingerprint, groupIndex, pageCount],
    ledgerStatus: (accountId) => ['ledgerStatus', accountId],
    /**
     * Persisted allocation proposals for one PAGE GROUP of
     * `AllocationSectionTable`. Position-keyed on `(filterFingerprint,
     * groupIndex, pageCount)` rather than on the matching line ids - see
     * `bankStatementLineAllocationDetails` above for the full reasoning, which
     * applies identically here (this read's `include` predicate also reads
     * `ledgerEntrySetId`).
     */
    bankLineProposalsByLines: (accountId, filterFingerprint, groupIndex, pageCount) => ['bankLineProposalsByLines', accountId, filterFingerprint, groupIndex, pageCount],
    /**
     * Async scorer run status for a bank statement (PENDING | COMPUTING | READY | FAILED).
     * Used to drive the progress indicator on the statement detail page.
     * 404 = no run for this statement (legacy / no import event).
     */
    proposalRun: (accountId, statementId) => ['proposalRun', accountId, statementId],
    clientStatementSnapshots: (accountId, clientId) => ['clientStatementSnapshots', accountId, clientId],
    /** A client's live debit-positive trust balance in the account's TRUST ledger. */
    clientTrustBalance: (accountId, clientId) => ['clientTrustBalance', accountId, clientId],
    /** A client's available credit sources (open credit notes + unallocated remainder) for the apply-credit funding picker. */
    availableCredit: (accountId, clientId) => ['availableCredit', accountId, clientId],
    /**
     * Prefix for the client statement page's single read (below). Exists so a
     * mutation that changes a client's statement (e.g. an invoice correction)
     * can invalidate every date-range variant with one prefix match, without
     * having to call the full key with `from`/`to` omitted - `clientStatementFeeds`
     * always returns a 5-element key, so an omitted `from`/`to` would come
     * through as an explicit `undefined` that fails TanStack Query's partial
     * match against a cached key holding real date strings there.
     */
    clientStatementFeedsPrefix: (accountId, clientId) => ['clientStatementFeeds', accountId, clientId],
    /** A client's combined business + trust statement feed (interleaved entries, per-ledger headers, position block). */
    clientStatementFeeds: (accountId, clientId, from, to) => [...queryKeys.clientStatementFeedsPrefix(accountId, clientId), from, to],
    /** A client's unpaid invoices statement as at a date; under the feeds prefix so a statement-changing mutation refreshes it too. */
    clientUnpaidInvoicesStatement: (accountId, clientId, to, clientProjectId) => [...queryKeys.clientStatementFeedsPrefix(accountId, clientId), 'unpaid-invoices', to, clientProjectId],
    /** Two-section trust creditors report for a TRUST ledger (assets by account, creditors by client). */
    trustCreditors: (accountId, ledgerId, at) => ['trustCreditors', accountId, ledgerId, asOfReportKey(at)],
    /** Ledger-scoped prefix matching every as-at variant of `trustCreditors` (see `trialBalancePrefix`). */
    trustCreditorsPrefix: (accountId, ledgerId) => ['trustCreditors', accountId, ledgerId],
    /** The multi-date trust reconciliation - own namespace, see `trialBalanceSeries`. */
    trustCreditorsSeries: (accountId, ledgerId, at, compare) => ['trustCreditorsSeries', accountId, ledgerId, asOfReportKey(at), compare],
    /** Ledger-scoped prefix matching every anchor and comparison. */
    trustCreditorsSeriesPrefix: (accountId, ledgerId) => ['trustCreditorsSeries', accountId, ledgerId],
    invoiceSettings: (accountId) => ['invoiceSettings', accountId],
    clientProjectReferenceSettings: (accountId) => ['clientProjectReferenceSettings', accountId],
    activityDocuments: (accountId, activityId) => ['activityDocuments', accountId, activityId],
    /** Account-scoped prefix of every activity's document list. */
    activityDocumentsPrefix: (accountId) => ['activityDocuments', accountId],
    assetSupportingDocumentsPrefix: (accountId) => ['assetSupportingDocuments', accountId],
    assetSupportingDocuments: (accountId, ledgerId, assetId) => [...queryKeys.assetSupportingDocumentsPrefix(accountId), ledgerId, assetId],
    bankStatementLineDocuments: (accountId, bankStatementLineId) => ['bankStatementLineDocuments', accountId, bankStatementLineId],
    bankStatementLineDocumentCounts: (accountId) => ['bankStatementLineDocumentCounts', accountId],
    /** Account-scoped prefix of every notification query, for invalidating the list and the count together. */
    notifications: (accountId) => ['notifications', accountId],
    notificationList: (accountId, options) => ['notifications', accountId, 'list', options?.unreadOnly ?? false, options?.category ?? null],
    notificationUnreadCount: (accountId) => ['notifications', accountId, 'unread-count'],
    /** User-scoped, not account-scoped: delivery choices follow the person across every account. */
    notificationPreferences: () => ['notification-preferences'],
    notificationDeliveryHours: () => ['notification-delivery-hours'],
    /** Keyed by user: the code belongs to whoever is signed in, and a tab can change user without a reload. */
    referralCode: (userId) => ['referral-code', userId],
    /** Public: keyed by the link's token, since the page has no session. */
    notificationUnsubscribeLink: (token) => ['notification-unsubscribe-link', token],
    accountSearch: (accountId, options) => ['accountSearch', accountId, options.q, options.include ?? null, options.limit ?? null],
};
